<template>
    <InfoBox title="Flash Info">
        <div class="px-5">
            <p>
                <strong>Detected by:</strong> SFDP
                <i v-if="this.flash.sfdp" class="bi bi-check"></i>
                <i v-else class="bi bi-x"></i>
                RDID
                <i v-if="this.flash.rdid" class="bi bi-check"></i>
                <i v-else class="bi bi-x"></i>
                <span v-if="capacitySource">
                    &middot; <strong>Size source:</strong> {{ capacitySource }}
                    <span v-if="sizeBytes">({{ sizeHuman }}, {{ sizeBytes }} bytes)</span>
                </span>
            </p>

            <p v-if="addressing">
                <strong>Addressing:</strong> {{ addressing.addressBytes }}-byte
                <span v-if="addressing.method !== '3-byte'">
                    via {{ addressing.method }}
                    (read 0x{{ hex(addressing.read) }}, program 0x{{ hex(addressing.program) }}, erase 0x{{ hex(addressing.erase4k) }})
                </span>
                <template v-if="addressing.reason">
                    <br/><span class="text-sm opacity-70">{{ addressing.reason }}</span>
                </template>
            </p>

            <p v-if="this.flash.rdid">
                <strong>RDID Information:</strong><br/>
                Vendor: {{ this.flash.rdid.vendor || 'Unknown' }}
                (0x{{ this.flash.rdid.vendor_id.toString(16).toUpperCase() }}) &middot;
                Type: 0x{{ this.flash.rdid.type.toString(16).toUpperCase() }} &middot;
                Capacity byte: 0x{{ this.flash.rdid.capacity_code.toString(16).toUpperCase() }}
                <span v-if="this.flash.rdid.capacityBytes">
                    ({{ formatRdidCapacity }})
                </span>
                <span v-else>(not a standard density code)</span>
            </p>
            <p v-else>
                <strong>RDID Information:</strong> no valid RDID response.
            </p>
            <div v-if="this.flash.sfdp">
                <strong>SFDP Information:</strong>
                <div class="table-responsive">
                <table class="font-mono text-sm text-left">
                    <tbody>
                    <tr>
                        <td>Address Bytes</td>
                        <td class="pl-2 pr-3">{{ this.flash.sfdp.addressBytes }}</td>
                        <td class="border-l border-grey border-opacity-25 pr-2 pl-3">Address Bytes Raw</td>
                        <td>{{ this.flash.sfdp.addressBytesRaw }}</td>
                    </tr>
                    <tr>
                        <td>Block/Sector Erase Sizes</td>
                        <td class="pl-2 pr-3">{{ this.flash.sfdp.bsEraseSizes }}</td>
                        <td class="border-l border-grey border-opacity-25 pr-2 pl-3">Erase Opcode 4K</td>
                        <td>{{ this.flash.sfdp.eraseOpcode4k }}</td>
                    </tr>
                    <tr>
                        <td>JEDEC ID</td>
                        <td class="pl-2 pr-3">{{ this.flash.sfdp.jedecId }}</td>
                        <td class="border-l border-grey border-opacity-25 pr-2 pl-3">Major Revision</td>
                        <td>{{ this.flash.sfdp.majorRevision }}</td>
                    </tr>
                    <tr>
                        <td>Minor Revision</td>
                        <td class="pl-2 pr-3">{{ this.flash.sfdp.minorRevision }}</td>
                        <td class="border-l border-grey border-opacity-25 pr-2 pl-3">Number of Parameter Headers (NPH)</td>
                        <td>{{ this.flash.sfdp.nph }}</td>
                    </tr>
                    <tr>
                        <td>Parameter Length</td>
                        <td class="pl-2 pr-3">{{ this.flash.sfdp.parameterLength }}</td>
                        <td class="border-l border-grey border-opacity-25 pr-2 pl-3">Parameter Major Revision</td>
                        <td>{{ this.flash.sfdp.parameterMajorRevision }}</td>
                    </tr>
                    <tr>
                        <td>Parameter Minor Revision</td>
                        <td class="pl-2 pr-3">{{ this.flash.sfdp.parameterMinorRevision }}</td>
                        <td class="border-l border-grey border-opacity-25 pr-2 pl-3">Parameter Table Pointer (PTP)</td>
                        <td>{{ this.flash.sfdp.ptp }}</td>
                    </tr>
                    <tr>
                        <td>Signature</td>
                        <td class="pl-2 pr-3">{{ this.flash.sfdp.signature }}</td>
                        <td class="border-l border-grey border-opacity-25 pr-2 pl-3">Write Enable Instruction Required</td>
                        <td>{{ this.flash.sfdp.writeEnableInstructionRequired }}</td>
                    </tr>
                    <tr>
                        <td>Write Enable Opcode</td>
                        <td class="pl-2 pr-3">{{ this.flash.sfdp.writeEnableOpcode }}</td>
                        <td class="border-l border-grey border-opacity-25 pr-2 pl-3">Write Granularity</td>
                        <td>{{ this.flash.sfdp.writeGranularity }}</td>
                    </tr>
                    <tr>
                        <td>Density</td>
                        <td class="pl-2 pr-3">{{ this.flash.sfdp.density }}</td>
                        <td class="border-l border-grey border-opacity-25 pr-2 pl-3">Vendor</td>
                        <td>0x{{ this.flash.sfdp.vendorId.toString(16) }}</td>
                    </tr>
                    <tr>
                        <td colspan="2"></td> <!-- Empty space for alignment -->
                    </tr>
                    </tbody>
                </table>
                </div>
            </div>
        </div>

        
    </InfoBox>
</template>

<script>
import { formatMemorySize } from "../flash/util"
import InfoBox from "./InfoBox.vue";
export default {
  components: {
      InfoBox,
  },
  props: {
    flash: {
        type: Object,
        required: true
    }
  },
  methods: {
    hex(value) {
      return value.toString(16).toUpperCase().padStart(2, '0');
    }
  },
  computed: {
    size() {
      if (!this.flash) return 0;
      try {
        return formatMemorySize(this.flash.getCapacity() * 8);
      } catch {
        return 'unknown';
      }
    },
    sizeBytes() {
      try {
        return this.flash.getCapacity();
      } catch {
        return null;
      }
    },
    sizeHuman() {
      return this.size;
    },
    capacitySource() {
      return this.flash && this.flash.getCapacitySource
        ? this.flash.getCapacitySource()
        : null;
    },
    addressing() {
      try {
        return this.flash.getAddressing();
      } catch {
        return null;
      }
    },
    formatRdidCapacity() {
      if (!this.flash.rdid || !this.flash.rdid.capacityBytes) return '';
      return formatMemorySize(this.flash.rdid.capacityBytes * 8);
    }
  }
};
</script>

<style scoped>
/* Table styling */

td,th {
  text-align: left;
}

</style>
